import React, { useEffect, useState } from 'react';
import Header from './components/Header';
import Hero from './components/Hero';
import StoryGrid from './components/StoryGrid';
import CuratorSidebar from './components/CuratorSidebar';
import ShopSection from './components/ShopSection';
import QuizModal from './components/QuizModal';
import ArticleModal from './components/ArticleModal';
import AuthorModal from './components/AuthorModal';
import SearchPanel from './components/SearchPanel';
import AboutSection from './components/AboutSection';
import Footer from './components/Footer';
import CartDrawer from './components/CartDrawer';
import SeasonalCampaigns from './components/SeasonalCampaigns';
import MaintenancePage from './components/MaintenancePage';
import MaintenanceAdmin from './components/MaintenanceAdmin';
import { stories } from './data/content';
import {
  getMaintenanceMode,
  setMaintenanceMode,
} from './services/configurationService';
import './App.css';
import './typography.css';
import './sky-blue.css';
import './feature.css';
import './seasonal.css';
import './maintenance.css';

function App() {
  const [quizOpen, setQuizOpen] = useState(false);
  const [activeStory, setActiveStory] = useState(null);
  const [activeAuthor, setActiveAuthor] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [cartOpen, setCartOpen] = useState(false);
  const [cart, setCart] = useState([]);
  const [savedStories, setSavedStories] = useState([]);
  const [maintenanceMode, setMaintenanceModeState] = useState(false);
  const [maintenanceReady, setMaintenanceReady] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);

  useEffect(() => {
    let active = true;
    let hasLoadedConfiguration = false;

    async function loadConfiguration() {
      try {
        const configuredMaintenanceMode = await getMaintenanceMode();

        if (active) {
          setMaintenanceModeState(configuredMaintenanceMode);
          setMaintenanceReady(true);
          hasLoadedConfiguration = true;
        }
      } catch {
        if (active && !hasLoadedConfiguration) {
          setMaintenanceModeState(false);
          setMaintenanceReady(true);
        }
      }
    }

    try {
      setCart(JSON.parse(localStorage.getItem('girl-in-paris-cart') || '[]'));
      setSavedStories(
        JSON.parse(localStorage.getItem('girl-in-paris-saved') || '[]'),
      );
    } catch {
      setCart([]);
      setSavedStories([]);
    }

    loadConfiguration();
    const configurationInterval = window.setInterval(loadConfiguration, 15000);

    return () => {
      active = false;
      window.clearInterval(configurationInterval);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem('girl-in-paris-cart', JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    localStorage.setItem(
      'girl-in-paris-saved',
      JSON.stringify(savedStories),
    );
  }, [savedStories]);

  const openShop = () => {
    document.getElementById('shop')?.scrollIntoView({
      behavior: 'smooth',
    });
  };

  const navigateTo = (target) => {
    document.getElementById(target)?.scrollIntoView({
      behavior: 'smooth',
    });
  };

  const openSearch = () => {
    setSearchQuery('');
    setSearchOpen(true);
  };

  const addToCart = (tier) => {
    setCart((items) => {
      const existing = items.find((item) => item.name === tier.name);

      if (existing) {
        return items.map((item) =>
          item.name === tier.name
            ? { ...item, quantity: item.quantity + 1 }
            : item,
        );
      }

      return [...items, { ...tier, quantity: 1 }];
    });

    setCartOpen(true);
  };

  const updateQuantity = (name, quantity) => {
    setCart((items) =>
      quantity < 1
        ? items.filter((item) => item.name !== name)
        : items.map((item) =>
            item.name === name ? { ...item, quantity } : item,
          ),
    );
  };

  const toggleSaved = (title) => {
    setSavedStories((items) =>
      items.includes(title)
        ? items.filter((item) => item !== title)
        : [...items, title],
    );
  };

  const closeSearch = () => {
    setSearchOpen(false);
    setSearchQuery('');
  };

  const toggleMaintenance = async () => {
    const nextValue = !maintenanceMode;
    setMaintenanceModeState(nextValue);

    try {
      await setMaintenanceMode(nextValue);
    } catch (error) {
      setMaintenanceModeState(!nextValue);
      alert(`Unable to save maintenance mode: ${error.message}`);
    }
  };

  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  if (!maintenanceReady) {
    return null;
  }

  if (maintenanceMode && !adminOpen) {
    return (
      <>
        <MaintenancePage onAdmin={() => setAdminOpen(true)} />
        {adminOpen && (
          <MaintenanceAdmin
            maintenanceMode={maintenanceMode}
            onToggle={toggleMaintenance}
            onClose={() => setAdminOpen(false)}
          />
        )}
      </>
    );
  }

  return (
    <div className="app-shell">
      <Header
        onShop={openShop}
        cartCount={cartCount}
        onCart={() => setCartOpen(true)}
        onSearch={openSearch}
      />

      <main>
        <Hero onRead={() => setActiveStory(stories[0])} />
        <SeasonalCampaigns onNavigate={navigateTo} />

        <div className="editorial-layout" id="edit">
          <StoryGrid
            onOpen={setActiveStory}
            savedStories={savedStories}
            onToggleSave={toggleSaved}
            onOpenAuthor={setActiveAuthor}
          />
          <CuratorSidebar />
        </div>

        <div className="interlude">
          <span>La prochaine vie commence ici.</span>
          <p>Your next life begins here.</p>
        </div>

        <ShopSection
          onQuiz={() => setQuizOpen(true)}
          onAdd={addToCart}
        />
        <AboutSection />
      </main>

      <Footer onAdmin={() => setAdminOpen(true)} />

      <QuizModal
        open={quizOpen}
        onClose={() => setQuizOpen(false)}
      />

      <ArticleModal
        story={activeStory}
        onClose={() => setActiveStory(null)}
        onOpenAuthor={(author) => {
          setActiveStory(null);
          setActiveAuthor(author);
        }}
        onShop={() => {
          setActiveStory(null);
          setTimeout(openShop, 100);
        }}
      />

      <AuthorModal
        author={activeAuthor}
        onClose={() => setActiveAuthor(null)}
        onOpenStory={(story) => {
          setActiveAuthor(null);
          setActiveStory(story);
        }}
      />

      <SearchPanel
        open={searchOpen}
        query={searchQuery}
        onQueryChange={setSearchQuery}
        onClose={closeSearch}
        onOpenStory={(story) => {
          closeSearch();
          setActiveStory(story);
        }}
        onOpenAuthor={(author) => {
          closeSearch();
          setActiveAuthor(author);
        }}
      />

      <CartDrawer
        open={cartOpen}
        cart={cart}
        onClose={() => setCartOpen(false)}
        onUpdate={updateQuantity}
      />

      {adminOpen && (
        <MaintenanceAdmin
          maintenanceMode={maintenanceMode}
          onToggle={toggleMaintenance}
          onClose={() => setAdminOpen(false)}
        />
      )}
    </div>
  );
}

export default App;